import React, { useEffect, useState, useMemo } from "react";
import { Link2, Plus, QrCode, Copy, Check, Trash2, ToggleLeft, ToggleRight, Eye, Filter, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const WhatsAppIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

/**
 * Admin tab: gestione link condivisibili pubblici per l'outlet.
 *
 * Funzionalità:
 * - Crea un nuovo link nominato con scelta tra "tutti i prodotti" o selezione curata
 * - Lista link con URL · views · ultima visualizzazione · stato attivo
 * - Copia URL · Scarica QR · Condividi su WhatsApp · Toggle attivo/disattivo · Elimina
 * - Modifica selezione prodotti dopo la creazione
 */
export default function AdminShareLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [products, setProducts] = useState([]);
  const [curatedMode, setCuratedMode] = useState(false);
  const [createSelectedIds, setCreateSelectedIds] = useState([]);
  const [editingLink, setEditingLink] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/share-links");
      setLinks(data);
    } catch (e) {
      toast.error("Errore caricamento link");
    } finally {
      setLoading(false);
    }
  }

  async function loadProducts() {
    try {
      const { data } = await api.get("/products");
      // Solo prodotti con stock > 0, ordinati per nome
      const list = (data || []).filter((p) => Number(p.stock || 0) > 0).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setProducts(list);
    } catch (e) {
      // ignore
    }
  }

  useEffect(() => {
    reload();
    loadProducts();
  }, []);

  async function create() {
    const trimmed = (name || "").trim();
    setCreating(true);
    try {
      const payload = { name: trimmed };
      if (curatedMode && createSelectedIds.length > 0) {
        payload.selected_product_ids = createSelectedIds;
      }
      const { data } = await api.post("/admin/share-links", payload);
      toast.success(`Link creato: ${data.name}`);
      setName("");
      setCuratedMode(false);
      setCreateSelectedIds([]);
      await reload();
      copyUrl(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione");
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(link) {
    try {
      await api.patch(`/admin/share-links/${link.id}`, { is_active: !link.is_active });
      toast.success(link.is_active ? "Link disattivato" : "Link riattivato");
      await reload();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  }

  async function remove(link) {
    if (!window.confirm(`Eliminare definitivamente il link "${link.name}"?\n(le statistiche di visite andranno perse)`)) return;
    try {
      await api.delete(`/admin/share-links/${link.id}`);
      toast.success("Link eliminato");
      await reload();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  }

  async function copyUrl(link) {
    try {
      await navigator.clipboard.writeText(link.share_url);
      setCopiedId(link.id);
      toast.success("URL copiato negli appunti");
      setTimeout(() => setCopiedId(null), 2500);
    } catch {
      toast.error("Copia fallita");
    }
  }

  function downloadQR(link) {
    const token = localStorage.getItem("ah_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}${link.qr_url}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `archihub-qr-${(link.name || link.id).replace(/[^a-z0-9]/gi, "_").slice(0, 40)}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("QR scaricato");
      })
      .catch(() => toast.error("Errore download QR"));
  }

  function shareWhatsApp(link) {
    const txt = `Ciao! Ecco una selezione di prodotti dal nostro outlet ARCHI-HUB:\n${link.share_url}\n\nPuoi prenotare per 48 ore lasciando un recapito.`;
    const url = `https://wa.me/?text=${encodeURIComponent(txt)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function saveCuratedSelection(link, newIds) {
    try {
      await api.patch(`/admin/share-links/${link.id}`, { selected_product_ids: newIds });
      toast.success(newIds.length === 0 ? "Selezione rimossa: ora mostra tutto l'outlet" : `Selezione aggiornata: ${newIds.length} prodotti`);
      setEditingLink(null);
      await reload();
    } catch (e) {
      toast.error("Errore aggiornamento selezione");
    }
  }

  return (
    <div className="mt-8 space-y-6" data-testid="admin-share-links">
      <div className="bg-white border border-ink/10 rounded-sm p-6">
        <div className="flex items-center gap-2 mb-2">
          <Link2 size={18} className="text-gold" />
          <h2 className="font-display text-2xl text-ink">Link Outlet Pubblici</h2>
        </div>
        <p className="text-sm text-slate1 mb-5 leading-relaxed max-w-3xl">
          Crea un link condivisibile (e QR code) che porta a una pagina pubblica dell&apos;outlet con
          <strong> Listino + Prezzo Studio</strong>, senza richiedere registrazione e senza accesso
          al resto della piattaforma. Il cliente finale può <strong>prenotare per 48 ore</strong>
          lasciando email o telefono — riceverai una notifica via email e nella campanella admin.
        </p>

        <div className="space-y-3" data-testid="share-link-create-form">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">
                Nome del link (per uso interno)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="es. Offerta giugno · WhatsApp"
                className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream text-sm focus:outline-none focus:border-ink"
                data-testid="share-link-name-input"
              />
            </div>
            <button
              type="button"
              onClick={create}
              disabled={creating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream rounded-sm text-[11px] tracking-[0.2em] uppercase hover:bg-ink/90 disabled:opacity-50"
              data-testid="share-link-create-btn"
            >
              <Plus size={14} /> {creating ? "Creazione..." : "Crea link"}
            </button>
          </div>

          {/* Curated selection toggle */}
          <div className="flex items-center gap-2">
            <label className="text-[11px] inline-flex items-center gap-2 cursor-pointer text-slate1 hover:text-ink">
              <input
                type="checkbox"
                checked={curatedMode}
                onChange={(e) => { setCuratedMode(e.target.checked); if (!e.target.checked) setCreateSelectedIds([]); }}
                className="accent-ink"
                data-testid="share-link-curated-toggle"
              />
              <Filter size={11} />
              <span className="tracking-[0.15em] uppercase">Limita a una selezione di prodotti</span>
              {curatedMode && (
                <span className="ml-2 px-2 py-0.5 bg-gold/20 text-ink text-[10px] tracking-[0.1em] uppercase rounded-sm">
                  {createSelectedIds.length} {createSelectedIds.length === 1 ? "prodotto" : "prodotti"}
                </span>
              )}
            </label>
          </div>

          {curatedMode && (
            <ProductPicker
              products={products}
              selectedIds={createSelectedIds}
              onChange={setCreateSelectedIds}
            />
          )}
        </div>
      </div>

      {/* Tabella link */}
      <div className="bg-white border border-ink/10 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate1 text-sm">Caricamento...</div>
        ) : links.length === 0 ? (
          <div className="p-8 text-center text-slate1 italic">
            Nessun link creato. Crea il primo qui sopra.
          </div>
        ) : (
          <div className="divide-y divide-ink/10">
            {links.map((l) => (
              <div
                key={l.id}
                className={`p-5 flex flex-col md:flex-row md:items-center gap-4 ${l.is_active ? "" : "bg-cream2/50 opacity-60"}`}
                data-testid={`share-link-row-${l.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-display text-base text-ink truncate">{l.name}</span>
                    {l.is_active ? (
                      <span className="text-[9px] tracking-[0.15em] uppercase bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-sm">Attivo</span>
                    ) : (
                      <span className="text-[9px] tracking-[0.15em] uppercase bg-slate-200 text-slate-700 px-2 py-0.5 rounded-sm">Disattivato</span>
                    )}
                    {Array.isArray(l.selected_product_ids) && l.selected_product_ids.length > 0 ? (
                      <span className="text-[9px] tracking-[0.15em] uppercase bg-gold/20 text-ink px-2 py-0.5 rounded-sm inline-flex items-center gap-1">
                        <Filter size={9} /> {l.selected_product_ids.length} prodotti
                      </span>
                    ) : (
                      <span className="text-[9px] tracking-[0.15em] uppercase bg-ink/10 text-slate1 px-2 py-0.5 rounded-sm">Tutti i prodotti</span>
                    )}
                  </div>
                  <div className="text-xs text-slate1 font-mono break-all">{l.share_url}</div>
                  <div className="mt-1 text-[11px] text-slate1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Eye size={11} /> <span className="tabular-nums">{l.views_count || 0}</span> {l.views_count === 1 ? "visita" : "visite"}
                    </span>
                    {l.last_viewed_at && (
                      <span>Ultima: {new Date(l.last_viewed_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>
                    )}
                    <span>Creato: {new Date(l.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => copyUrl(l)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-ink/20 text-ink rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-cream2 transition-colors"
                    data-testid={`share-link-copy-${l.id}`}
                  >
                    {copiedId === l.id ? <><Check size={12} className="text-emerald-600" /> Copiato</> : <><Copy size={12} /> Copia</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => shareWhatsApp(l)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-emerald-300 text-emerald-700 rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-emerald-50 transition-colors"
                    title="Condividi su WhatsApp"
                    data-testid={`share-link-whatsapp-${l.id}`}
                  >
                    <WhatsAppIcon size={12} /> WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadQR(l)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-ink/20 text-ink rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-cream2 transition-colors"
                    data-testid={`share-link-qr-${l.id}`}
                  >
                    <QrCode size={12} /> QR
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingLink(l)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-ink/20 text-ink rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-cream2 transition-colors"
                    title="Modifica selezione prodotti"
                    data-testid={`share-link-edit-${l.id}`}
                  >
                    <Edit3 size={12} /> Selezione
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleActive(l)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-ink/20 text-ink rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-cream2 transition-colors"
                    data-testid={`share-link-toggle-${l.id}`}
                  >
                    {l.is_active ? <><ToggleRight size={14} className="text-emerald-600" /> Attivo</> : <><ToggleLeft size={14} /> Off</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(l)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-rose-300 text-rose-700 rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-rose-50 transition-colors"
                    data-testid={`share-link-delete-${l.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit selection modal */}
      {editingLink && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditingLink(null)} data-testid="edit-selection-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl max-h-[90vh] rounded-sm overflow-hidden flex flex-col">
            <div className="p-5 border-b border-ink/10 flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-1">Modifica selezione</div>
                <div className="font-display text-xl text-ink">{editingLink.name}</div>
                <div className="text-xs text-slate1 mt-1">Lascia vuoto per mostrare tutti i prodotti dell&apos;outlet.</div>
              </div>
              <button onClick={() => setEditingLink(null)} className="text-slate1 hover:text-ink" data-testid="edit-selection-close">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <EditableProductPicker
                products={products}
                initialSelectedIds={editingLink.selected_product_ids || []}
                onConfirm={(ids) => saveCuratedSelection(editingLink, ids)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductPicker({ products, selectedIds, onChange }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return products;
    return products.filter((p) => (p.name || "").toLowerCase().includes(k) || (p.brand || "").toLowerCase().includes(k) || (p.category || "").toLowerCase().includes(k));
  }, [products, q]);

  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="border border-ink/10 rounded-sm bg-cream2 p-3" data-testid="share-link-product-picker">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Cerca prodotto da includere..."
        className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm focus:outline-none focus:border-ink mb-3"
        data-testid="share-link-product-picker-search"
      />
      <div className="max-h-64 overflow-y-auto space-y-1">
        {filtered.length === 0 ? (
          <div className="text-xs text-slate1 italic px-2 py-3">Nessun prodotto trovato.</div>
        ) : filtered.map((p) => {
          const checked = selectedIds.includes(p.id);
          return (
            <label
              key={p.id}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer text-sm ${checked ? "bg-gold/10" : "hover:bg-white"}`}
              data-testid={`share-link-product-${p.id}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(p.id)}
                className="accent-ink"
              />
              <span className="flex-1 truncate">
                <span className="text-slate1 mr-1">{p.brand}</span>
                <span className="text-ink">{p.name}</span>
              </span>
              <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">{p.category}</span>
            </label>
          );
        })}
      </div>
      {selectedIds.length > 0 && (
        <div className="mt-2 pt-2 border-t border-ink/10 flex items-center justify-between text-[11px]">
          <span className="text-slate1">{selectedIds.length} selezionati</span>
          <button type="button" onClick={() => onChange([])} className="text-rose-600 hover:underline">Pulisci selezione</button>
        </div>
      )}
    </div>
  );
}

function EditableProductPicker({ products, initialSelectedIds, onConfirm }) {
  const [sel, setSel] = useState(initialSelectedIds);
  return (
    <div className="space-y-4">
      <ProductPicker products={products} selectedIds={sel} onChange={setSel} />
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-ink/10">
        <button
          type="button"
          onClick={() => onConfirm([])}
          className="px-4 py-2 border border-ink/20 text-slate1 rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-cream2"
          data-testid="edit-selection-reset"
        >
          Mostra tutto l&apos;outlet
        </button>
        <button
          type="button"
          onClick={() => onConfirm(sel)}
          className="px-4 py-2 bg-ink text-cream rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-ink/90"
          data-testid="edit-selection-save"
        >
          Salva selezione ({sel.length})
        </button>
      </div>
    </div>
  );
}
